import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import {
  useGetMeasurementUnitsList,
  useGetTargetList,
} from '@/modules/workingDay/apiHooks';
import { IWorkingDay } from '@/modules/workingDay/types';
import { InputField, SelectField } from '@/shared/components/atoms';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { IGeneral } from '@/shared/types';

const TargetSetting = () => {
  const { t } = useTranslation('common');
  const [selectedAsset] = useStore((store) => store?.asset);

  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<IWorkingDay.IOperationFormValuesSchema>();

  const { data: unitsList, isLoading: isUnitsLoading } =
    useGetMeasurementUnitsList(selectedAsset?.id);
  const {
    data: target,
    fetchNextPage,
    isLoading: isTargetLoading,
  } = useGetTargetList();
  const targetList = target?.pages.flatMap((page) => page.items);

  return (
    <div className='flex flex-col gap-16'>
      <SelectField
        id='measurementUnit'
        errorMessageId='measurementUnit.id'
        getLabel={(unit: IGeneral.NameIdPair) => unit.name}
        getValue={(unit: IGeneral.NameIdPair) => unit}
        value={unitsList?.filter(
          (unit) => unit.id === watch('measurementUnit')?.id
        )}
        isDisabled={isUnitsLoading}
        borderColor={themeColors().outline.variant}
        label={t('productivity-unit')}
        placeholder={t('productivity-unit')}
        list={unitsList ?? []}
        py='0.5rem'
        px='0.5rem'
        borderRadius='1rem'
        maxMenuHeight={200}
        width='w-full min-w-0'
      />
      <InputField
        id='averageTargetPerDay'
        {...register('averageTargetPerDay', {
          setValueAs: (value) => (value?.length ? value : undefined),
        })}
        label={t('productivity-target-per-worker')}
        placeholder={t('productivity-target-per-worker')}
        className='w-full !rounded-xl bg-background-transparent desktop:max-h-56'
        labelClasseName='!text-14'
        borderColor='border-outline-variant'
        textColor='text-text-default'
        width='w-full'
        type='number'
        error={!!errors.averageTargetPerDay}
        errorMessage={errors.averageTargetPerDay?.message}
        py='!py-14'
      />
      <InfiniteSelectField
        id='targetDuration'
        errorMessageId='targetDuration.id'
        getLabel={(unit: IGeneral.NameIdPair) => unit.name}
        getValue={(unit: IGeneral.NameIdPair) => unit}
        value={targetList?.filter(
          (target) => target.id === watch('targetDuration')?.id
        )}
        isDisabled={isTargetLoading}
        borderColor={themeColors().outline.variant}
        label={t('target-duration')}
        placeholder={t('target-duration')}
        list={targetList ?? []}
        py='0.5rem'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full min-w-0'
        maxMenuHeight={200}
        fetchNextPage={fetchNextPage}
      />
      <InputField
        id='note'
        {...register('note', {
          setValueAs: (value) => (value?.length ? value : undefined),
        })}
        label={`${t('notes')} (${t('optional')})`}
        error={!!errors?.note}
        errorMessage={errors.note?.message}
        placeholder={`${t('notes')} (${t('optional')})`}
        className='w-full !rounded-xl bg-background-transparent desktop:max-h-56'
        labelClasseName='!text-14'
        borderColor='border-outline-variant'
        textColor='text-text-default'
        width='w-full'
        py='!py-14'
      />
    </div>
  );
};
export { TargetSetting };
